import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { DriverEvaluationReportResponse } from '../../core/models/portal.models';

@Component({
  selector: 'app-driver-evaluation-report-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './driver-evaluation-report-page.component.html'
})
export class DriverEvaluationReportPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);

  protected readonly report = signal<DriverEvaluationReportResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly lessonId = signal<number | null>(null);
  protected readonly finalOnly = signal(false);
  protected readonly visibleAttempts = computed(() => {
    const report = this.report();
    if (!report) {
      return [];
    }

    if (this.finalOnly()) {
      return report.attempts.filter((attempt) => attempt.type === 'FINAL');
    }

    const lessonId = this.lessonId();
    return lessonId === null
      ? report.attempts
      : report.attempts.filter((attempt) => attempt.lessonId === lessonId);
  });
  protected readonly reportTitle = computed(() => {
    if (this.finalOnly()) {
      return 'Abschluss-Wissenscheck';
    }

    const lessonId = this.lessonId();
    const lessonAttempt = this.report()?.attempts.find((attempt) => attempt.lessonId === lessonId);
    return lessonAttempt?.lessonTitle ?? 'Alle Fehlversuche';
  });

  private driverId = 0;
  private enrollmentId = 0;

  ngOnInit(): void {
    this.driverId = Number(this.route.snapshot.paramMap.get('driverId'));
    this.enrollmentId = Number(this.route.snapshot.paramMap.get('enrollmentId'));
    this.route.queryParamMap.subscribe((params) => {
      const lessonId = Number(params.get('lessonId'));
      this.lessonId.set(Number.isFinite(lessonId) && lessonId > 0 ? lessonId : null);
      this.finalOnly.set(params.get('type') === 'FINAL');
    });
    this.loadReport();
  }

  protected formatAttemptDate(value: string | null): string {
    if (!value) {
      return 'Zeitpunkt nicht verfügbar';
    }

    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value));
  }

  protected score(value: number | null): string {
    return value === null ? '-' : `${value} %`;
  }

  private loadReport(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.driverEvaluationReport(this.driverId, this.enrollmentId).subscribe({
      next: (report) => {
        this.report.set(report);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Der Auswertungsbericht konnte nicht geladen werden.');
        this.loading.set(false);
      }
    });
  }
}
